import { Inject, Injectable, Optional } from '@angular/core';
import {
  BulkActionInstanceStatusInterface,
  BulkActionsSdkStatusService,
  BulkActionStatusService,
} from '@vendasta/bulk-actions';
import { combineLatest, Observable, of, ReplaySubject, SchedulerLike, throwError } from 'rxjs';
import { catchError, filter, map, publishReplay, refCount, share, switchMap, takeWhile } from 'rxjs/operators';
import { ProgressReporter, withPolling } from './progress-reporter.service';

export interface ActiveWorkReporter {
  readonly hasActiveWork$: Observable<boolean>;
}

@Injectable({ providedIn: 'root' })
export class BulkActionsProgressReporter implements ProgressReporter, ActiveWorkReporter {
  readonly hasActiveWork$: Observable<boolean>;
  private readonly actionListId$$ = new ReplaySubject<string>(1);
  private readonly instancesProgress$: Observable<BulkActionInstanceStatusInterface[]>;

  constructor(
    @Inject(BulkActionsSdkStatusService)
    private readonly bulkActionsSdk: BulkActionStatusService,
    @Inject('PARTNER_ID') private partnerId$: Observable<string>,
    @Optional() @Inject('') readonly scheduler?: SchedulerLike,
    @Optional() @Inject('') readonly pollInterval?: number,
  ) {
    this.pollInterval = pollInterval || 5000;
    this.scheduler = this.scheduler || undefined; // rxjs can't handle null
    this.instancesProgress$ = this.buildInstanceProgress();
    this.hasActiveWork$ = this.instancesProgress$.pipe(map((v) => v.findIndex((p) => p.isFinalized !== true) > -1));
  }

  private buildInstanceProgress(): Observable<BulkActionInstanceStatusInterface[]> {
    return combineLatest([this.partnerId$, this.actionListId$$]).pipe(
      // FIXME: users with limited market access are denied access to statuses only; they still have access to the lists
      // TODO: make a decision on how lists work for users who do not have access to all markets
      switchMap(([pid, alId]) =>
        withPolling(() => this.bulkActionsSdk.list(pid, undefined, alId), this.pollInterval, this.scheduler),
      ),
      catchError((e) => {
        console.log('Error is:', e);
        if (e.status === 404) {
          return of([]);
        }
        return throwError(e);
      }),
      share(),
    );
  }

  bindToActionList(actionListId: string): void {
    this.actionListId$$.next(actionListId);
  }

  getPolledStatus$(instanceId: string): Observable<BulkActionInstanceStatusInterface> {
    return this.instancesProgress$.pipe(
      map((p) => p.find((i) => i.instanceId === instanceId)),
      filter((p) => p !== undefined),
      takeWhile((v) => v.processedItems < v.totalItems || !v.isFinalized, true),
      publishReplay(1),
      refCount(),
    );
  }
}
